/**
 * Utilitaires pour détecter les intersections géométriques entre portes et parois
 * Utilisé pour découper/masquer automatiquement les parois dans les zones de portes
 */

export interface Point3D {
  x: number;
  y: number;
  z: number;
}

export interface Point2D {
  x: number;
  z: number;
}

export interface Rectangle3D {
  coins: Point3D[];
  centre: Point3D;
  normale: Point3D;
  largeur: number;
  hauteur: number;
}

export interface PanneauParoi {
  id: string;
  geometrie: {
    coins: Point3D[];
    centre: Point3D;
    normale: Point3D;
    surface: number;
  };
  vertices: number[];
  indices: number[];
}

export interface ElementPorte {
  id: string;
  geometrie: {
    coins: Point3D[];
    centre: Point3D;
    normale: Point3D;
    surface: number;
  };
  parametres: {
    style: string;
    largeur: number;
    hauteur: number;
    decalageSol: number;
  };
}

/**
 * Classe utilitaire pour les calculs d'intersection géométrique
 */
export class IntersectionUtils {

  /**
   * Teste si une porte intersecte avec un panneau de paroi
   * @param porte - Élément porte à tester
   * @param panneau - Panneau de paroi à tester
   * @returns true si intersection détectée
   */
  static detecterIntersectionPorteParoi(porte: ElementPorte, panneau: PanneauParoi): boolean {
    try {
      // Vérifier si les normales sont coplanaires (à peu près parallèles)
      const angleNormales = this.calculerAngleEntreNormales(porte.geometrie.normale, panneau.geometrie.normale);
      const tolerance = Math.PI / 4; // 45 degrés de tolérance
      
      if (angleNormales > tolerance) {
        return false; // Pas dans le même plan
      }

      // Projeter les géométries sur le plan XZ pour simplifier les calculs
      const rectanglePorte = this.projeterSurPlanXZ(porte.geometrie.coins);
      const polygonePanneau = this.projeterSurPlanXZ(panneau.geometrie.coins);

      // Vérifier intersection 2D
      return this.testIntersectionRectanglePolygone(rectanglePorte, polygonePanneau);

    } catch (error) {
      console.warn('Erreur détection intersection porte-paroi:', error);
      return false;
    }
  }

  /**
   * Calcule l'angle entre deux vecteurs normaux
   */
  private static calculerAngleEntreNormales(n1: Point3D, n2: Point3D): number {
    const dot = n1.x * n2.x + n1.y * n2.y + n1.z * n2.z;
    const mag1 = Math.sqrt(n1.x * n1.x + n1.y * n1.y + n1.z * n1.z);
    const mag2 = Math.sqrt(n2.x * n2.x + n2.y * n2.y + n2.z * n2.z);
    
    if (mag1 === 0 || mag2 === 0) return Math.PI;
    
    const cosAngle = Math.max(-1, Math.min(1, dot / (mag1 * mag2)));
    return Math.acos(Math.abs(cosAngle)); // Utiliser valeur absolue pour ignorer l'orientation
  }

  /**
   * Projette les points 3D sur le plan XZ (ignore Y)
   */
  private static projeterSurPlanXZ(points: Point3D[]): Point2D[] {
    return points.map(p => ({ x: p.x, z: p.z }));
  }

  /**
   * Teste si un rectangle intersecte avec un polygone en 2D
   * Utilise l'algorithme Separating Axis Theorem (SAT)
   */
  private static testIntersectionRectanglePolygone(rectangle: Point2D[], polygone: Point2D[]): boolean {
    // Simplification: vérifier si les bounding boxes se chevauchent
    const rectBounds = this.calculerBoundingBox2D(rectangle);
    const polyBounds = this.calculerBoundingBox2D(polygone);

    return this.boundingBoxesIntersectent(rectBounds, polyBounds);
  }

  /**
   * Calcule la bounding box 2D d'un ensemble de points
   */
  private static calculerBoundingBox2D(points: Point2D[]): { minX: number; maxX: number; minZ: number; maxZ: number } {
    if (points.length === 0) {
      return { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
    }

    let minX = points[0].x, maxX = points[0].x;
    let minZ = points[0].z, maxZ = points[0].z;

    for (const point of points) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minZ = Math.min(minZ, point.z);
      maxZ = Math.max(maxZ, point.z);
    }

    return { minX, maxX, minZ, maxZ };
  }

  /**
   * Teste si deux bounding boxes 2D se chevauchent
   */
  private static boundingBoxesIntersectent(
    bb1: { minX: number; maxX: number; minZ: number; maxZ: number },
    bb2: { minX: number; maxX: number; minZ: number; maxZ: number }
  ): boolean {
    return !(bb1.maxX < bb2.minX || bb2.maxX < bb1.minX ||
             bb1.maxZ < bb2.minZ || bb2.maxZ < bb1.minZ);
  }

  /**
   * Filtre les panneaux de parois qui intersectent avec les portes
   * @param panneaux - Liste des panneaux de parois
   * @param portes - Liste des éléments de portes
   * @returns Panneaux qui n'intersectent avec aucune porte
   */
  static filtrerPanneauxSansIntersection(panneaux: PanneauParoi[], portes: ElementPorte[]): PanneauParoi[] {
    return panneaux.filter(panneau => {
      // Vérifier si ce panneau intersecte avec au moins une porte
      return !portes.some(porte => this.detecterIntersectionPorteParoi(porte, panneau));
    });
  }

  /**
   * Retourne les panneaux de parois qui intersectent avec les portes
   * @param panneaux - Liste des panneaux de parois
   * @param portes - Liste des éléments de portes
   * @returns Panneaux qui intersectent avec au moins une porte
   */
  static obtenirPanneauxIntersectants(panneaux: PanneauParoi[], portes: ElementPorte[]): PanneauParoi[] {
    return panneaux.filter(panneau => {
      return portes.some(porte => this.detecterIntersectionPorteParoi(porte, panneau));
    });
  }

  /**
   * Calcule les zones d'exclusion pour les portes (pour découpe des traverses)
   * @param portes - Liste des éléments de portes
   * @param margeSecurite - Marge en mètres autour de l'ouverture (défaut: 0.005m = 5mm)
   * @returns Zones rectangulaires à exclure
   */
  static calculerZonesExclusionPortes(portes: ElementPorte[], margeSecurite: number = 0.005): Rectangle3D[] {
    return portes.map(porte => {
      const coins = porte.geometrie.coins;
      
      // Agrandir la zone avec la marge de sécurité
      const centre = porte.geometrie.centre;
      const largeurAvecMarge = porte.parametres.largeur + (margeSecurite * 2);
      const hauteurAvecMarge = porte.parametres.hauteur + (margeSecurite * 2);

      // Recalculer les coins avec la marge
      const demiLargeur = largeurAvecMarge / 2;
      const demiHauteur = hauteurAvecMarge / 2;

      const coinsAvecMarge: Point3D[] = [
        { x: centre.x - demiLargeur, y: centre.y - demiHauteur, z: centre.z },
        { x: centre.x + demiLargeur, y: centre.y - demiHauteur, z: centre.z },
        { x: centre.x + demiLargeur, y: centre.y + demiHauteur, z: centre.z },
        { x: centre.x - demiLargeur, y: centre.y + demiHauteur, z: centre.z }
      ];

      return {
        coins: coinsAvecMarge,
        centre,
        normale: porte.geometrie.normale,
        largeur: largeurAvecMarge,
        hauteur: hauteurAvecMarge
      };
    });
  }
}